import { Button, Modal } from "@mantine/core";
import type React from "react";
import type { SessionHistoryItem } from "../../../types/portal";
import { formatCurrency } from "../../../utils/formatting";
import { getStatusTextColor } from "../../../utils/status";
import DeliverablesView from "../../shared/DeliverablesView";
import EmptyState from "../../shared/EmptyState";
import StarRating from "../../shared/StarRating";

const normalizeTrainingTasks = (rawTasks: unknown): string[] => {
  if (!rawTasks) return [];
  let parsed: unknown = rawTasks;
  if (typeof rawTasks === "string") {
    try {
      parsed = JSON.parse(rawTasks);
    } catch {
      return [rawTasks];
    }
  }
  if (!Array.isArray(parsed)) parsed = [parsed];
  return (parsed as unknown[])
    .map((task) => {
      if (typeof task === "string") return task;
      if (task && typeof task === "object") {
        return (task as { description?: string }).description || "";
      }
      return "";
    })
    .filter(Boolean);
};

interface ClientSessionsTabProps {
  sessionHistory: SessionHistoryItem[];
  loading: boolean;
  error: string | null;
  selectedSession: SessionHistoryItem | null;
  showSessionModal: boolean;
  onRefresh: () => void;
  onRateSession: (session: SessionHistoryItem, value: number) => void;
  onViewSessionDetails: (session: SessionHistoryItem) => void;
  onCloseSessionModal: () => void;
}

const ClientSessionsTab: React.FC<ClientSessionsTabProps> = ({
  sessionHistory,
  loading,
  error,
  selectedSession,
  showSessionModal,
  onRefresh,
  onRateSession,
  onViewSessionDetails,
  onCloseSessionModal,
}) => (
  <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
      <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
        Session History
      </h2>
      <Button
        onClick={onRefresh}
        disabled={loading}
        radius="md"
        className={loading ? "cursor-not-allowed opacity-60" : ""}
      >
        Refresh
      </Button>
    </div>
    {loading ? (
      <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
        Loading sessions...
      </div>
    ) : error ? (
      <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
        {error}
      </div>
    ) : sessionHistory.length === 0 ? (
      <EmptyState
        icon="No Sessions"
        description={
          <>
            <p>No sessions completed yet</p>
            <p className="text-[0.9rem] mt-2.5 text-muted-foreground">
              Book a session with an active trainer to get started!
            </p>
          </>
        }
      />
    ) : (
      <div className="flex flex-col gap-4">
        {sessionHistory.map((session) => {
          const sessionDate = session.date ? new Date(session.date) : null;
          const sessionEndDate = session.end_date ? new Date(session.end_date) : null;
          const formattedDate = sessionDate ? sessionDate.toLocaleDateString() : "N/A";
          const formattedTime = sessionDate ? sessionDate.toLocaleTimeString() : "N/A";
          const formattedEndTime = sessionEndDate ? sessionEndDate.toLocaleTimeString() : null;

          return (
            <div
              key={session.id}
              className="bg-muted rounded-lg px-3 sm:px-5 py-4 border border-border mb-3 transition-all duration-300 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 w-full">
                <div className="min-w-[150px]">
                  <div className="text-base font-semibold text-foreground mb-1">
                    Session #{session.id.slice(-8)}
                  </div>
                  <div className="text-[0.85rem] text-muted-foreground">{formattedDate}</div>
                </div>
                <div className="flex-1 flex items-center flex-wrap text-[0.9rem]">
                  <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">
                    Trainer:{" "}
                  </span>
                  <span className="text-[0.9rem] text-foreground font-medium">
                    {session.trainer_name}
                  </span>
                  <span className="text-muted-foreground mx-2"> | </span>
                  <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">Time: </span>
                  <span className="text-[0.9rem] text-foreground font-medium">
                    {formattedTime}
                    {formattedEndTime ? ` – ${formattedEndTime}` : ""}
                  </span>
                  <span className="text-muted-foreground mx-2"> | </span>
                  <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">
                    Duration:{" "}
                  </span>
                  <span className="text-[0.9rem] text-foreground font-medium">
                    {session.duration_minutes || session.session_duration_minutes || 0} minutes
                  </span>
                  <span className="text-muted-foreground mx-2"> | </span>
                  <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">Price: </span>
                  <span className="text-[0.9rem] text-foreground font-medium">
                    {formatCurrency(session.price)}
                  </span>
                  {session.status && (
                    <>
                      <span className="text-muted-foreground mx-2"> | </span>
                      <span className="text-[0.9rem] text-muted-foreground font-medium mr-1">
                        Status:{" "}
                      </span>
                      <span
                        className={`text-[0.9rem] font-medium ${getStatusTextColor(session.status)}`}
                      >
                        {session.status}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex flex-col gap-2.5 items-start sm:items-end">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[0.85rem] text-foreground font-medium">
                      Rate this session:{" "}
                    </span>
                    <StarRating
                      rating={session.user_rating || 0}
                      onRate={(value) => onRateSession(session, value)}
                      showValue={Boolean(session.user_rating)}
                    />
                  </div>
                  <span className="inline-flex items-center bg-success/15 text-success py-0.5 px-2.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                    Completed
                  </span>
                  <Button size="sm" radius="md" onClick={() => onViewSessionDetails(session)}>
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}

    <Modal
      opened={showSessionModal && selectedSession !== null}
      onClose={onCloseSessionModal}
      title="Session Details"
      size="lg"
      classNames={{
        header: "bg-card border-b border-border",
        title: "text-2xl font-semibold text-foreground",
        body: "bg-card p-6",
        close: "text-muted-foreground hover:text-foreground",
        content: "bg-card border border-border",
      }}
    >
      {selectedSession && (
        <>
          <div className="flex flex-col gap-3 mb-6 pb-6 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="text-[0.95rem] font-semibold text-muted-foreground">
                Session ID:
              </span>
              <span className="text-[0.95rem] text-foreground">
                #{selectedSession.id.slice(-8)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[0.95rem] font-semibold text-muted-foreground">Date:</span>
              <span className="text-[0.95rem] text-foreground">
                {selectedSession.date
                  ? new Date(selectedSession.date).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[0.95rem] font-semibold text-muted-foreground">Time:</span>
              <span className="text-[0.95rem] text-foreground">
                {selectedSession.date
                  ? new Date(selectedSession.date).toLocaleTimeString()
                  : "N/A"}
                {selectedSession.end_date
                  ? ` – ${new Date(selectedSession.end_date).toLocaleTimeString()}`
                  : ""}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[0.95rem] font-semibold text-muted-foreground">
                Trainer:
              </span>
              <span className="text-[0.95rem] text-foreground">
                {selectedSession.trainer_name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[0.95rem] font-semibold text-muted-foreground">
                Duration:
              </span>
              <span className="text-[0.95rem] text-foreground">
                {selectedSession.duration_minutes ||
                  selectedSession.session_duration_minutes ||
                  0}{" "}
                minutes
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[0.95rem] font-semibold text-muted-foreground">Price:</span>
              <span className="text-[0.95rem] text-foreground">
                {formatCurrency(selectedSession.price)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[0.95rem] font-semibold text-muted-foreground">
                Status:
              </span>
              <span
                className={`text-[0.95rem] ${getStatusTextColor(selectedSession.status || "")}`}
              >
                {selectedSession.status}
              </span>
            </div>
          </div>

          {(() => {
            const checklist = normalizeTrainingTasks(selectedSession.training_tasks);
            const hasContent = Boolean(selectedSession.summary) || checklist.length > 0;
            return (
              <DeliverablesView
                deliverables={undefined}
                fallbackSummary={selectedSession.summary}
                fallbackChecklist={checklist}
                showPending={selectedSession.status === "completed" && !hasContent}
              />
            );
          })()}

          <div className="pt-5 border-t border-border flex justify-end">
            <Button radius="md" onClick={onCloseSessionModal}>
              Close
            </Button>
          </div>
        </>
      )}
    </Modal>
  </div>
);

export default ClientSessionsTab;
